import * as React from "react";
import { useEffect, useState } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBriefcase,
  faCalendarDays,
  faFilter,
  faFilterSlash,
  faLink,
  faPeopleGroup,
  faSearch,
} from "@fortawesome/sharp-regular-svg-icons";
import { library } from "@fortawesome/fontawesome-svg-core";
import {
  Alert,
  Box,
  Button,
  focusRing,
  Grid2 as Grid,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Typography,
} from "@platform/style";
import { TemplateV2 } from "../../../models/TemplateV2";
import { WorkGroup } from "../../../models/WorkGroup";
import { fetchWithToken } from "@platform/core";
import {
  WORK_MANAGEMENT_ADMIN_BFF_API_BASE,
  WORK_MANAGEMENT_ADMIN_BFF_API_BASE_V2,
} from "../../../platform-workitems";
import { reportError } from "../../../utils/errors";
import { WorkGroupRequest } from "../../../models/WorkGroupRequest";
import { PutTemplateRequestV2 } from "../../../models/PutTemplateRequestV2";

library.add(faFilter, faCalendarDays, faFilterSlash, faSearch, faLink, faBriefcase);

interface VerifyConfigurationProps {
  onHandleBack: () => void;
  onHandleFinish: () => void;
  onHandleSnackBar?: (message: string, open: boolean) => void;
  onHandleErrorMessage: (message: string | null) => void;
  onTemplateUpdate: any;
  onGroupUpdate: any;
  refetchTemplates: () => void;
  refetchGroups: () => void;
  snackBar?: { SnackBarMessage: string; ShowSnackBarMessage: boolean };
  errorMessage: string | null;
  selectedTemplate?: TemplateV2;
  selectedGroups?: WorkGroup[];
  selectedIntegratedSystem: string;
}

const ERROR_MESSAGE_UNABLE_TO_SAVE_WORK_CONFIG = "Unable to save Work Configuration.";

export default function VerifyConfiguration(props: Readonly<VerifyConfigurationProps>) {
  const [selectedGroups] = useState<WorkGroup[] | undefined>(props.selectedGroups);
  const [template, setTemplate] = useState<TemplateV2 | undefined>(props.selectedTemplate);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [disableFinish, setDisableFinish] = useState(false);

  useEffect(() => {
    if (props.errorMessage === ERROR_MESSAGE_UNABLE_TO_SAVE_WORK_CONFIG) {
      handleCloseErrorMessage();
    }
    if (!loadingGroups && !loadingTemplate) {
      setDisableFinish(false);
    } else {
      setDisableFinish(true);
    }
  }, [loadingGroups, loadingTemplate]);

  function extractErrorMessageText(error: { message: any }) {
    let message = "An error occurred processing the request. Please try again.";
    const errorMessage = error.message;
    const start = errorMessage.indexOf("ErrorMessageText");

    if (start != -1) {
      const end = errorMessage.indexOf("}");
      const sentence = errorMessage.substring(start, end);
      const errorElements = sentence.split(":");
      message = errorElements[1].replaceAll('"', "");
    }

    return message;
  }

  const handleCloseErrorMessage = () => {
    props.onHandleErrorMessage(null);
  };

  const handleTemplateChange = (fieldName: string, value: any) => {
    if (template) {
      setTemplate({
        ...template,
        [fieldName]: value,
      });
    }
  };

  const handleFinish = async () => {
    const groupIds = selectedGroups
      ? selectedGroups
          ?.filter((group: { WorkGroupIdentifier: string }) => group.WorkGroupIdentifier !== "")
          .map((group) => group.WorkGroupIdentifier)
      : [];

    const newGroups = selectedGroups
      ? selectedGroups?.filter(
          (group: { WorkGroupIdentifier: string }) => group.WorkGroupIdentifier == ""
        )
      : [];

    if (newGroups?.length > 0 && template) {
      for (const group of newGroups) {
        const templates: string[] = [template.TemplateCode];
        const workGroupResponse = await createWorkGroup(group, templates);

        if (workGroupResponse) {
          const workGroupId = workGroupResponse.WorkGroupIdentifier;
          groupIds.push(workGroupId);
        }
      }
    }

    await updateTemplateConfig(groupIds);

    props.onTemplateUpdate(template);
    props.onGroupUpdate(selectedGroups);
    props.onHandleFinish();
  };

  async function createWorkGroup(group: any, templates: string[]) {
    const request = buildWorkGroup(group, templates);
    return await createWorkGroupRequest(request);
  }

  function buildWorkGroup(group: any, templates: string[]) {
    const request: WorkGroupRequest = {
      Name: group.Name,
      Description: group.Description,
      ResourceIdentifiers: group.Resources.map(
        (resource: { ResourceIdentifier: any }) => resource.ResourceIdentifier
      ),
      TemplateCodes: templates,
    };
    return request;
  }

  async function createWorkGroupRequest(workGroupRequest: WorkGroupRequest) {
    setLoadingGroups(true);

    return fetchWithToken(`${WORK_MANAGEMENT_ADMIN_BFF_API_BASE}/work-groups`, {
      method: "POST",
      body: JSON.stringify(workGroupRequest),
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(
            JSON.stringify({
              statusCode: response.status,
              statusText: response.statusText,
              body: await response.json(),
            })
          );
        }
        if (props.onHandleSnackBar) {
          props.onHandleSnackBar(`Work Group ${workGroupRequest?.Name} saved successfully.`, true);
        }
        return response.json();
      })
      .then((data) => {
        return data;
      })
      .catch((error) => {
        props.onHandleErrorMessage(extractErrorMessageText(error));
        reportError(extractErrorMessageText(error));
      })
      .finally(() => {
        setLoadingGroups(false);
      });
  }

  async function updateTemplateConfig(groupIds: string[]) {
    if (template) {
      const templateRequest = buildTemplateRequest(template, groupIds);
      await updateTemplateRequest(templateRequest);
    }
  }

  function buildTemplateRequest(template: TemplateV2, groupIds: string[]) {
    const templateRequest: PutTemplateRequestV2 = {
      EnabledIndicator: template.EnabledIndicator,
      Title: template.Title,
      Description: template.Description,
      Priority: template.Priority,
      ServiceLevelInDays: template.ServiceLevelInDays,
      AvailableToUserIndicator: template.AvailableToUserIndicator,
      IntegratedSystem: props.selectedIntegratedSystem,
      CreateWorkInIntegratedSystemIndicator:
        props.selectedIntegratedSystem == "NONE"
          ? false
          : template.CreateWorkInIntegratedSystemIndicator,
      IntegratedSystemKeys: template.IntegratedSystemKeys,
      AssignmentGroupIdentifiers: groupIds,
    };
    return templateRequest;
  }

  async function updateTemplateRequest(templateRequest: PutTemplateRequestV2) {
    setLoadingTemplate(true);

    await fetchWithToken(
      `${WORK_MANAGEMENT_ADMIN_BFF_API_BASE_V2}/templates/${template?.TemplateCode}`,
      {
        method: "PUT",
        body: JSON.stringify(templateRequest),
      }
    )
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(
            JSON.stringify({
              statusCode: response.status,
              statusText: response.statusText,
              body: await response.json(),
            })
          );
        }
        if (props.onHandleSnackBar) {
          props.onHandleSnackBar(`Work Configuration saved successfully.`, true);
        }
        props.refetchTemplates();
        props.refetchGroups();
      })
      .catch((error) => {
        props.onHandleErrorMessage(extractErrorMessageText(error));
        reportError(extractErrorMessageText(error));
      })
      .finally(() => {
        setLoadingTemplate(false);
      });
  }

  return (
    <Paper
      elevation={0}
      variant="outlined"
      min-height="100vh"
      sx={{
        backgroundColor: "inherit",
        display: "flex",
        flexDirection: "column",
        border: "none",
        paddingLeft: "8px",
        paddingRight: "8px",
      }}
    >
      <Grid container>
        {props.errorMessage && (
          <Alert
            onClose={handleCloseErrorMessage}
            severity="error"
            sx={{
              width: "calc(100% - 34px)",
              borderColor: "error.main",
              borderWidth: "1px",
              borderStyle: "solid",
            }}
          >
            {props.errorMessage}
          </Alert>
        )}

        <Grid size={{ sm: 12, xs: 12 }}>
          <Typography
            aria-label={"Verify Configuration Header"}
            style={{ paddingTop: "15px", fontWeight: "bold", textAlign: "left" }}
          >
            Verify Configuration
          </Typography>
        </Grid>
        <Grid size={{ sm: 5, xs: 11 }}>
          <Typography style={{ textAlign: "left" }}>Your Work Groups will be linked to</Typography>
          <Box
            color={"secondary"}
            style={{
              textAlign: "left",
              backgroundColor: "#E3F3FA",
              overflowY: "auto",
              height: "30vh",
              padding: "12px",
            }}
          >
            <Typography variant="h6" sx={{ paddingBottom: "15px", color: "black" }}>
              <FontAwesomeIcon
                icon={faPeopleGroup}
                style={{ paddingRight: "12px", color: "#000000" }}
              />{" "}
              Work Groups
            </Typography>
            {selectedGroups?.map((group, index) => (
              <Box key={index} sx={{ paddingLeft: "32px", color: "black" }}>
                <Typography variant="h6">{group?.Name}</Typography>
                <Typography variant="subtitle2" sx={{ paddingBottom: "15px" }}>
                  People: {group?.Resources?.length}
                </Typography>
              </Box>
            ))}
          </Box>
          <Typography>You can edit your groups anytime.</Typography>
        </Grid>
        <Grid size={{ xs: 1 }} sx={{ margin: "auto", textAlign: "center" }}>
          <FontAwesomeIcon icon={faLink} style={{ fontSize: "xx-large", rotate: "45deg" }} />
        </Grid>
        <Grid
          size={{ xs: 11, sm: 5 }}
          sx={{
            height: "20px",
            color: "#000000",
            fontSize: "15px",
            fontWeight: "600",
            letterSpacing: "0",
            lineHeight: "20px",
          }}
        >
          <Typography>Your Work Templates</Typography>
          <Box
            style={{
              textAlign: "left",
              backgroundColor: "#E3F3FA",
              overflowY: "auto",
              height: "30vh",
              padding: "12px",
            }}
          >
            <Typography variant="h6">
              <FontAwesomeIcon icon={faBriefcase} style={{ paddingRight: "12px" }} /> Template
            </Typography>
            <Box sx={{ paddingLeft: "32px" }}>
              <Typography variant="subtitle2" sx={{ paddingBottom: "15px" }}>
                {template?.Title}
              </Typography>
              <Typography variant="h6">Template Description</Typography>
              <Typography variant="subtitle2" sx={{ paddingBottom: "15px" }}>
                {template?.Description}
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                sx={{ paddingBottom: "15px", alignItems: "center" }}
              >
                <Typography variant="h6">SLA</Typography>
                <Typography variant="subtitle2" sx={{ color: "black" }}>
                  {template?.ServiceLevelInDays}
                </Typography>
                <Typography variant="h6">Priority</Typography>
                <Typography variant="subtitle2" sx={{ color: "black" }}>
                  {template?.Priority}
                </Typography>
              </Stack>
              {template?.IntegratedSystem ? (
                <>
                  <Typography variant="h6">Work Integration Systems</Typography>
                  {template?.IntegratedSystemKeys?.length > 0 ? (
                    <>
                      {template?.IntegratedSystemKeys?.map((integration, index) => (
                        <Box key={index} sx={{ color: "black" }}>
                          <Typography variant="subtitle2">
                            Integrated System: {template?.IntegratedSystem ?? "N/A"}
                          </Typography>
                          <Typography variant="subtitle2" sx={{ paddingBottom: "15px" }}>
                            Integrated System Value: {integration.Value ?? "N/A"}
                          </Typography>
                        </Box>
                      ))}
                    </>
                  ) : (
                    "None"
                  )}
                </>
              ) : null}
            </Box>
          </Box>
          <Typography>You can edit your templates anytime.</Typography>
        </Grid>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={{ fontWeight: "bold" }}>Template Status: </Typography>
          <Typography>Inactive</Typography>
          <Switch
            aria-label={"Template Status"}
            checked={template?.EnabledIndicator}
            onChange={(e) => handleTemplateChange("EnabledIndicator", e.target.checked)}
            color="secondary"
          />
          <Typography>Active</Typography>
        </Stack>
        <Grid size={{ xs: 2.5 }} sx={{ mt: 2, display: "grid", marginLeft: "auto" }}>
          <Box
            sx={{
              bottom: "60px",
              display: "flex",
              flexDirection: "row",
              pt: 2,
              justifyContent: "flex-end",
            }}
          >
            <Button
              aria-label={"Verify Configuration Back"}
              variant="outlined"
              color={"primary"}
              sx={{ focusRing, marginRight: "5px" }}
              onClick={props.onHandleBack}
              disabled={disableFinish}
            >
              Back
            </Button>
            <Button
              aria-label={"Verify Configuration Finish"}
              variant="contained"
              color={"primary"}
              sx={focusRing}
              onClick={handleFinish}
              disabled={disableFinish || selectedGroups?.length == 0}
            >
              Finish
            </Button>
          </Box>
        </Grid>
        <Snackbar
          aria-label={"Verify Configuration Snackbar"}
          message={props.snackBar?.SnackBarMessage ?? ""}
          open={props.snackBar?.ShowSnackBarMessage || false}
          onClose={() => {
            if (props?.onHandleSnackBar) {
              props.onHandleSnackBar("", false);
            }
          }}
        />
      </Grid>
    </Paper>
  );
}
